import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPackage, FiPlus, FiCamera, FiCheck, FiAlertCircle, FiX } = FiIcons;

const LogDrops = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchCharacters();
      fetchRecentLogs();
    }
  }, [user]);

  useEffect(() => {
    if (selectedChar) fetchItemsForCharacter();
    else setItems([]);
  }, [selectedChar]);

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchCharacters = async () => {
    // Only fetch characters where the Master contract is ACTIVE
    const { data: activeMasterIds } = await supabase.from(TABLES.PILOT_MASTERS).select('master_id').eq('pilot_id', user.id).eq('status', 'active');
    const masterIds = (activeMasterIds || []).map(m => m.master_id);
    
    if (masterIds.length === 0) {
      setCharacters([]);
      return;
    }

    const { data } = await supabase.from(TABLES.CHARACTERS).select('*, master:master_id(id, nickname, username)').eq('pilot_id', user.id).in('master_id', masterIds);
    setCharacters(data || []);
  };

  const fetchItemsForCharacter = async () => {
    const char = characters.find(c => c.id === selectedChar);
    if (!char) return;
    const { data } = await supabase.from(TABLES.ITEMS).select('*').or(`master_id.eq.${char.master_id},master_id.is.null`).order('name', { ascending: true });
    setItems(data || []);
  };

  const fetchRecentLogs = async () => {
    const { data } = await supabase.from(TABLES.LOGS).select(`*, item:${TABLES.ITEMS}(name), char:${TABLES.CHARACTERS}(name)`).eq('pilot_id', user.id).order('created_at', { ascending: false }).limit(10);
    setRecentLogs(data || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem || !selectedChar) return;
    const char = characters.find(c => c.id === selectedChar);
    const item = items.find(i => i.id === selectedItem);
    setLoading(true);
    let proofUrl = null;
    try {
      if (file) {
        const fileName = `${Date.now()}-${user.id}.${file.name.split('.').pop()}`;
        const filePath = `drop-proofs/${user.id}/${fileName}`;
        const { error: uploadError } = await supabase.storage.from('payout-proofs').upload(filePath, file);
        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage.from('payout-proofs').getPublicUrl(filePath);
          proofUrl = publicUrl;
        }
      }
      const qty = parseInt(quantity) || 1;
      const { error } = await supabase.from(TABLES.LOGS).insert([{
        pilot_id: user.id, master_id: char.master_id, item_id: selectedItem, character_id: selectedChar, quantity: qty, value_at_log: item.rate * qty, status: 'pending_master', proof_url: proofUrl
      }]);
      if (error) throw error;
      
      notify(`Loot logged for ${char.master.nickname || char.master.username}!`);
      setSelectedItem('');
      setFile(null);
      setPreview(null);
      fetchRecentLogs();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 pb-20 relative">
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }} className={`fixed top-6 right-6 z-[100] px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 backdrop-blur-md ${notification.type === 'success' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-red-500/90 text-white border-red-400'}`}>
            <SafeIcon icon={notification.type === 'success' ? FiCheck : FiAlertCircle} />
            <span className="font-black text-xs uppercase tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-8">
        <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm">
          <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
            <SafeIcon icon={FiPackage} className="text-indigo-600" /> Log Loot
          </h2>
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Active Master Character</label>
              <div className="grid grid-cols-1 gap-3">
                {characters.length === 0 ? (
                  <p className="text-xs text-gray-400 p-4 border-2 border-dashed border-gray-100 rounded-2xl">No active Master teams found. Approve invitations from your dashboard.</p>
                ) : (
                  characters.map(char => (
                    <button key={char.id} type="button" onClick={() => setSelectedChar(char.id)} className={`p-4 rounded-2xl border-2 text-left transition-all ${selectedChar === char.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 hover:border-gray-200'}`}>
                      <div className={`font-black ${selectedChar === char.id ? 'text-indigo-600' : 'text-gray-900'}`}>{char.name}</div>
                      <div className="text-[9px] font-bold text-gray-400 uppercase">Master: {char.master?.nickname || char.master?.username}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
            {selectedChar && (
              <>
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Loot Item</label>
                  <select className="apple-input cursor-pointer" value={selectedItem} onChange={e => setSelectedItem(e.target.value)} required>
                    <option value="">Select item...</option>
                    {items.map(i => <option key={i.id} value={i.id}>{i.name} (₱{i.rate})</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Quantity</label>
                    <input type="number" className="apple-input font-black" value={quantity} onChange={e => setQuantity(e.target.value)} min="1" required />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Proof Screenshot</label>
                    <div className="relative">
                      <input type="file" id="drop-proof" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } }} className="hidden" />
                      <label htmlFor="drop-proof" className={`flex items-center justify-center h-[60px] cursor-pointer rounded-2xl border-2 border-dashed transition-all ${preview ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-gray-200 hover:border-indigo-400 text-gray-400'}`}>
                        <SafeIcon icon={FiCamera} className="mr-2" />
                        <span className="text-[10px] font-black uppercase text-center">{preview ? 'Attached' : 'Upload'}</span>
                      </label>
                    </div>
                  </div>
                </div>
                <button type="submit" disabled={loading || !selectedItem} className="apple-button-primary w-full !py-5 text-lg shadow-2xl">
                  {loading ? 'Logging Loot...' : 'Submit Loot Log'}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
      <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <h2 className="text-3xl font-black text-gray-900 mb-8">Recent Loot</h2>
        <div className="flex-1 space-y-6 overflow-y-auto no-scrollbar">
          {recentLogs.map(log => (
            <div key={log.id} className="p-6 bg-gray-50 rounded-3xl border border-transparent hover:border-gray-200 transition-all">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm overflow-hidden">
                    {log.proof_url ? <img src={log.proof_url} className="w-full h-full object-cover" /> : <FiPackage />}
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900">{log.item?.name} x{log.quantity}</h4>
                    <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{log.char?.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-emerald-600">₱{log.value_at_log?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LogDrops;